import type { ButtonHTMLAttributes, ReactNode } from "react";

import { CustomerCard, itemsLabel } from "../components/consultation";
import { Button } from "../components/ui/button";
import { Icon } from "../components/ui/icons";
import { Centered, IconBadge, Screen, SectionLabel, StatusPill } from "../components/ui/layout";
import { cn } from "../libs/cn";
import { elapsedSeconds, formatDuration, formatTime, formatTimer } from "../libs/format";
import type { ActivityScans } from "../modules/scans/api";
import type { StaffState } from "../modules/states/api";
import { outcomeItems } from "../modules/states/tree";

export interface Common {
  busy: boolean;
  error: string | null;
  now: number;
}

// ---------------------------------------------------------------- смена закрыта

export function ClosedScreen({ busy, error, onOpen, onStats }: Common & { onOpen: () => void; onStats: () => void }) {
  return (
    <Screen
      error={error}
      footer={
        <>
          <Button size="lg" loading={busy} onClick={onOpen}>
            Открыть смену
          </Button>
          <Button variant="ghost" size="sm" className="self-center" onClick={onStats}>
            Итоги сегодняшней смены
          </Button>
        </>
      }
    >
      <Centered>
        <IconBadge tone="muted" name="clock" size="lg" />
        <h1 className="text-2xl font-semibold">Смена закрыта</h1>
        <p className="max-w-72 text-center text-[var(--color-muted)]">
          Откройте смену в начале работы — дальше отмечайте, чем заняты: консультация, выкладка, приёмка, перерыв.
        </p>
      </Centered>
    </Screen>
  );
}

// ---------------------------------------------------------------- базовое состояние («В зале»)

export function HallScreen({
  busy,
  error,
  now,
  shiftOpenedAt,
  baseName,
  states,
  onEnter,
  onStats,
  onPickItems,
}: Common & {
  shiftOpenedAt: Date;
  baseName: string;
  states: StaffState[];
  onEnter: (stateId: string) => void;
  onStats: () => void;
  // Нет — в справочнике нет анкеты с подбором.
  onPickItems: (() => void) | null;
}) {
  // Главное действие зала — состояние с исходами (консультация клиента): оно внизу, под пальцем.
  // Остальные (перерыв, выкладка, приёмка…) — плитками выше.
  const primary = states.find((state) => outcomeItems(state.outcomes).length > 0) ?? null;
  const others = states.filter((state) => state !== primary);

  return (
    <Screen
      error={error}
      header={
        <>
          <StatusPill tone="accent">{baseName}</StatusPill>
          <Button variant="ghost" size="sm" onClick={onStats}>
            <Icon name="chart" className="size-5" />
            Мой день
          </Button>
        </>
      }
      footer={
        <>
          {/* Подбор не требует консультации — справочный инструмент, как сканер на главной. */}
          {onPickItems && (
            <Button variant="secondary" size="md" disabled={busy} onClick={onPickItems}>
              <Icon name="sparkle" className="size-5" />
              Подобрать изделие
            </Button>
          )}
          {primary && (
            <Button size="lg" className="min-h-22 text-xl" loading={busy} onClick={() => onEnter(primary.id)}>
              <Icon name={primary.icon ?? "user"} />
              {primary.name}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col items-center gap-1 pt-4 pb-8">
        <p className="text-sm text-[var(--color-muted)]">На смене</p>
        <p className="text-4xl font-semibold tracking-tight tabular-nums">
          {formatDuration(elapsedSeconds(shiftOpenedAt, now))}
        </p>
        <p className="text-sm text-[var(--color-muted)]">с {formatTime(shiftOpenedAt)}</p>
      </div>

      {others.length > 0 && (
        <>
          <SectionLabel>Чем заняты</SectionLabel>
          {/* Колонки по месту: при крупном системном шрифте плитки сами встают в один столбец, подписи
              переносятся, а не режутся. Нечётная последняя — на всю ширину, чтобы не висела одна в ряду. */}
          <div className="grid grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-2.5">
            {others.map((state) => (
              <Tile
                key={state.id}
                disabled={busy}
                className="min-w-0 last:odd:[grid-column:1/-1]"
                onClick={() => onEnter(state.id)}
              >
                <IconBadge tone="muted" name={state.icon} />
                <span className="min-w-0 text-left leading-tight [overflow-wrap:anywhere]">{state.name}</span>
              </Tile>
            ))}
          </div>
        </>
      )}

      {primary && (
        <p className="mt-auto pt-8 text-center text-sm text-[var(--color-muted)]">
          Подошли к покупателю — нажмите «{primary.name}».
        </p>
      )}
    </Screen>
  );
}

// ---------------------------------------------------------------- небазовое состояние (таймер)

// Состояние «занят» (консультация, выкладка…) — как в прототипе: экран в цвете состояния, по центру
// пульсирующий диск с таймером. Диск — та же кнопка «Завершить», просто крупнее и заметнее.
// В консультации под диском — клиент (по отсканированной карте или телефону) и счётчик отсканированных
// изделий; пока клиента нет — над «Завершить» кнопка «Ввести телефон». Больше ничего: экран должен
// оставаться пустым и спокойным.
export function InStateScreen({
  busy,
  error,
  now,
  stateName,
  icon,
  color,
  startedAt,
  hasOutcomes,
  scans,
  demand,
  onPhone,
  onPickItems,
  onFinish,
}: Common & {
  stateName: string;
  icon: string | null;
  color: string | null;
  startedAt: Date;
  hasOutcomes: boolean;
  // null — не консультация или сканы ещё не загружены.
  scans: ActivityScans | null;
  // Записанный запрос клиента после подбора без результата — строкой под счётчиком.
  demand: string | null;
  onPhone: () => void;
  // Нет — в справочнике нет анкеты с подбором; показывается только в консультации.
  onPickItems: (() => void) | null;
  onFinish: () => void;
}) {
  const tint = color || "var(--color-gold)";
  const timer = formatTimer(elapsedSeconds(startedAt, now));
  const customer = scans?.customer ?? null;
  const itemsCount = scans?.items.length ?? 0;

  return (
    <Screen
      error={error}
      tint={tint}
      header={<StatusPill tone="inverse">{stateName}</StatusPill>}
      footer={
        <>
          {hasOutcomes && onPickItems && (
            <Button
              size="md"
              disabled={busy}
              onClick={onPickItems}
              className="min-h-[min(3.5rem,9dvh)] bg-white/10 text-white ring-1 ring-white/50 active:bg-white/20"
            >
              <Icon name="sparkle" className="size-5" />
              Подобрать
            </Button>
          )}
          {hasOutcomes && !customer && (
            <Button
              size="md"
              disabled={busy}
              onClick={onPhone}
              className="min-h-[min(3.5rem,9dvh)] bg-white/10 text-white ring-1 ring-white/50 active:bg-white/20"
            >
              <Icon name="phone" className="size-5" />
              Ввести телефон
            </Button>
          )}
          <Button
            size="md"
            loading={busy}
            onClick={onFinish}
            // Белая кнопка, текст — цвет состояния потемнее; не понял color-mix — останется тёмный ink.
            className="min-h-[min(3.5rem,9dvh)] bg-white text-[var(--color-ink)] shadow-[0_16px_30px_-14px_rgba(0,0,0,0.45)] active:bg-white/90"
            style={{ color: `color-mix(in srgb, ${tint} 80%, black)` }}
          >
            <Icon name="check" />
            Завершить
          </Button>
          {/* На коротком экране (крупный шрифт + масштаб экрана) подсказка уступает место диску. */}
          <p className="text-center text-xs text-white/75 [@media(max-height:600px)]:hidden">
            {hasOutcomes ? "Дальше — отметить, чем закончилось" : "Вернётесь в зал"}
          </p>
        </>
      }
    >
      <Centered>
        <button
          type="button"
          onClick={onFinish}
          disabled={busy}
          aria-label={`${stateName}, ${timer}. Завершить`}
          // Диск не больше 250px и не больше 36 % высоты окна: на коротком экране (масштаб экрана +
          // крупный шрифт) он ужимается, а кнопки остаются на месте.
          className="pulse-ring relative grid size-[min(250px,36dvh)] place-items-center rounded-full transition-transform active:scale-[0.97] disabled:opacity-60"
        >
          <span className="flex size-[78%] flex-col items-center justify-center gap-1 rounded-full border-[1.5px] border-white/30 bg-white/10 backdrop-blur-sm">
            <Icon name={icon} className="mb-1 size-7 opacity-90" />
            <span className="max-w-40 truncate text-[11px] font-extrabold tracking-[0.18em] uppercase opacity-85">
              {stateName}
            </span>
            {/* Кегль растёт с системным шрифтом, но не больше 7 % высоты окна: «59:59» умещается в диск и на
                коротком экране при масштабе 160 %. */}
            <span className="text-[min(2.5rem,7dvh)] leading-none font-extrabold tabular-nums">{timer}</span>
          </span>
        </button>
        {customer ? (
          <div className="pt-2">
            <CustomerCard customer={customer} inverse />
          </div>
        ) : (
          // Подсказка уступает место записанному запросу: на коротком экране с тремя кнопками обоим не хватит.
          !demand && (
            <p className="max-w-72 pt-2 text-center text-sm text-white/75">
              Начато в {formatTime(startedAt)}. Время попадёт в итоги дня.
            </p>
          )
        )}
        {itemsCount > 0 && (
          <p className="text-xs font-bold tracking-wider text-white/80 uppercase">{itemsLabel(itemsCount)}</p>
        )}
        {demand && (
          <p className="max-w-72 pt-1 text-center text-xs text-white/80">
            <span className="font-bold tracking-wider uppercase">Запрос</span> · {demand}
          </p>
        )}
      </Centered>
    </Screen>
  );
}

// ---------------------------------------------------------------- общие мелочи

function Tile({
  selected,
  className,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      // Выбор иначе передавался бы только цветом — скринридер о нём не узнал бы.
      aria-pressed={selected}
      className={cn(
        "relative flex min-h-16 items-center gap-3 rounded-2xl px-3 py-3 text-base leading-tight font-medium ring-1 transition-[background-color,transform] select-none active:scale-[0.98] disabled:opacity-40",
        selected
          ? "bg-[var(--color-accent)] text-white ring-[var(--color-accent)]"
          : "bg-[var(--color-surface)] text-[var(--color-ink)] ring-[var(--color-line)] active:bg-[#ece8de]",
        className,
      )}
      {...props}
    >
      {selected && (
        <span className="absolute top-2 right-2">
          <Icon name="check" className="size-4" />
        </span>
      )}
      {children}
    </button>
  );
}
